/**
 * WebGL Background Presets — 10 fragment shaders, product-fit for ShootCreator.
 *
 * ## ストーリー: 「ノイズ → シグナル」
 * ShootCreatorは音声(ノイズ)を構造化データ(シグナル)に変換するツール。
 * 各プリセットはこの変換プロセスの異なる段階を視覚的に表現する:
 *   音声キャプチャ → 文字起こし → ミス分類 → パターン発見 → 洞察
 *
 * ## 技術基盤
 * - **FBM (Fractal Brownian Motion)**: ノイズを5オクターブ重ねて自然な揺らぎを作る
 * - **Domain Warping (Inigo Quilez)**: ノイズの入力座標をノイズで歪める → 有機的な流体模様
 * - **ACES Filmic Tone Mapping**: 映画品質の色再現。HDRカラーを自然に圧縮
 * - **IGN Dithering**: グラデーションの色段差(バンディング)を知覚不能レベルに除去
 * - **Vignette**: 画面端を暗くして視線を中央に集める
 *
 * ## レンダリング方式
 * Raw WebGL (Three.js不使用)。画面全体を覆う四角形(fullscreen quad)の
 * フラグメントシェーダーで全ピクセルの色を計算する。
 * canvas は mix-blend-mode: screen でコンテンツ上に重なる(黒=透明, 色=加算)。
 *
 * ## Uniform変数 (JSからシェーダーに送る値)
 * - u_time: 経過秒数 (アニメーション駆動)
 * - u_resolution: キャンバスのピクセルサイズ (アスペクト比計算用)
 * - u_mouse: マウス位置 (0.0~1.0, 左下が原点)
 * - u_scroll: スクロール進捗 (0.0=ページ先頭, 1.0=最下部)
 * - u_color1, u_color2: ルート別カラーパレット (classic=橙, ai=青紫 等)
 *
 * @see _claude_memo_/shared/26-0318-220000/220000-webgl-product-fit-spec.md
 */

// ─── Shared GLSL Building Blocks ────────────────────────────────────────────
// これらの文字列はGLSL (OpenGL Shading Language) のコード断片。
// frag() で結合して1つのフラグメントシェーダーを構成する。

/**
 * 頂点シェーダー (Vertex Shader)
 * 画面全体を覆う四角形の4頂点の位置とUV座標を処理する。
 * - attribute: JSから頂点バッファ経由で受け取るデータ
 * - varying: フラグメントシェーダーに渡す補間値
 * - vUv: テクスチャ座標 (0,0)=左下 → (1,1)=右上
 */
export const VERTEX_SHADER = `
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

/**
 * フラグメントシェーダー共通ヘッダー
 * - precision highp float: GPU浮動小数点の精度指定 (highp=32bit, mediump=16bit)
 *   モバイルGPUではhighpが使えない場合があるのでフォールバック
 * - uniform: JSから毎フレーム送られる値 (時間, マウス位置 等)
 * - varying vUv: 頂点シェーダーから補間されたUV座標
 */
const HEADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
#else
  precision mediump float;
#endif
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_scroll;
uniform vec3 u_color1;
uniform vec3 u_color2;
varying vec2 vUv;
`;

/**
 * Simplex 3D Noise (Ashima Arts, MIT License)
 *
 * 3次元空間の任意の点に対して -1.0 ~ 1.0 の滑らかな疑似ランダム値を返す。
 * Perlinノイズの改良版。格子アーティファクトが少なく、計算コストも低い。
 *
 * snoise(vec3 v) → float
 * - v.xy: 空間座標 (スケールで模様の細かさが変わる)
 * - v.z: 時間軸に使うことでアニメーションする
 *
 * 内部の mod289/permute/taylorInvSqrt はハッシュ関数の構成要素。
 */
const NOISE = `
vec3 mod289(vec3 x){ return x - floor(x*(1.0/289.0))*289.0; }
vec4 mod289(vec4 x){ return x - floor(x*(1.0/289.0))*289.0; }
vec4 permute(vec4 x){ return mod289(((x*34.0)+1.0)*x); }
vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314*r; }
float snoise(vec3 v){
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}
`;

/**
 * FBM (Fractal Brownian Motion) — フラクタルブラウン運動
 *
 * ノイズを異なるスケール（オクターブ）で5回重ね合わせ、
 * 自然な地形・雲・流体のような複雑な模様を作る。
 *
 * 仕組み:
 * 1. 大きなスケールのノイズ (振幅0.5) → 全体の形状
 * 2. 中スケールのノイズ (振幅0.25) → 中程度のディテール
 * 3. ... と繰り返す
 * 4. 各オクターブで回転行列(約27.5°)をかけ、格子状のアーティファクトを除去
 *
 * @param p - 2D座標 (スケールが大きいほど細かい模様)
 * @param z - 時間軸 (アニメーション用)
 * @returns -1.0 ~ 1.0 の値
 *
 * 5オクターブは品質/性能のスイートスポット。
 * 4だと粗い、6以上はモバイルGPUで重くなる。
 */
const FBM = `
float fbm(vec2 p, float z) {
  float val = 0.0;
  float amp = 0.5;
  mat2 rot = mat2(0.87758, 0.47943, -0.47943, 0.87758);
  for (int i = 0; i < 5; i++) {
    val += amp * snoise(vec3(p, z));
    p = rot * p * 2.0;
    amp *= 0.5;
  }
  return val;
}
`;

/**
 * ハッシュ関数 — 座標から疑似ランダム値を生成
 *
 * sin() + 大きな定数の乗算 → fract() で小数部だけ取得。
 * GPU上で高速に「ランダムっぽい」値を得る定番技法。
 *
 * hash(vec2) → float: 1つのランダム値
 * hash2(vec2) → vec2: 2つのランダム値 (Voronoiのセル中心位置等に使用)
 *
 * 注意: 暗号論的には安全ではないが、視覚効果には十分。
 */
const HASH = `
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}
vec2 hash2(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453123);
}
`;

/**
 * ポストプロセッシング — 最終色調整
 *
 * aces(): ACES Filmic Tone Mapping (Narkowicz近似)
 *   映画業界標準の色再現。HDRカラー(1.0超え)をSDR(0-1)に自然に圧縮。
 *   Three.jsも内蔵する同一アルゴリズム。
 *
 * ign(): Interleaved Gradient Noise (Jorge Jimenez, Call of Duty: Advanced Warfare)
 *   グラデーションの色段差(バンディング)を除去するディザリング。
 *   ランダムノイズより視覚的に心地よい規則的パターンを生成。
 *   1/255 (8bitの最小単位) の微小ノイズを加算することで、
 *   隣接ピクセル間の色差を知覚不能レベルに平滑化する。
 *
 * post(): 上記 + ビネット効果を適用
 *   ビネット: 画面端を暗くする → dot(q,q) で中心からの距離²を計算
 *   0.8の係数で控えめに適用（awwwards受賞サイトの標準的な強度）
 */
const POST = `
vec3 aces(vec3 x) {
  float a = 2.51; float b = 0.03;
  float c = 2.43; float d = 0.59; float e = 0.14;
  return clamp((x*(a*x+b))/(x*(c*x+d)+e), 0.0, 1.0);
}
float ign(vec2 coord) {
  return fract(52.9829189 * fract(dot(coord, vec2(0.06711056, 0.00583715))));
}
vec3 post(vec3 color, vec2 uv) {
  vec2 q = uv - 0.5;
  color *= 1.0 - dot(q, q) * 0.8;
  color = aces(color);
  color += (1.0/255.0) * ign(gl_FragCoord.xy) - (0.5/255.0);
  return color;
}
`;

/**
 * GLSL文字列パーツを結合して1つのフラグメントシェーダーにする。
 * 例: frag(HEADER, NOISE, FBM, POST, mainBody) → 完全なシェーダー文字列
 */
function frag(...parts: string[]): string {
  return parts.join("\n");
}

// ─── Preset Interface ───────────────────────────────────────────────────────

export interface WebGLPreset {
  /** プリセット番号 (1-10) */
  id: number;
  /** 英語名 (開発者向け) */
  name: string;
  /** 日本語名 (UIスイッチャー表示用) */
  nameJa: string;
  /** このプリセットが表現するShootCreatorのストーリー */
  motif: string;
  /** GLSLフラグメントシェーダーのソースコード */
  fragment: string;
}

// ─── 10 Product-Fit Presets ─────────────────────────────────────────────────

export const PRESETS: WebGLPreset[] = [
  /**
   * ━━━ 1. Voice Stream ━━━
   *
   * ストーリー: シューティング中のリアルタイム発話キャプチャ。
   * 「入った」「左すぎ」「ショート」「オーバー」——短い呟きが断続的に入り続ける。
   *
   * 技法:
   * - 画面いっぱいを使った大きな波形（振幅 = 画面の40%超）
   * - 高速なノイズ駆動波形 — 発話のリアルタイム入力感
   * - 発話バースト: 複数の「塊」が時間的に現れては消える
   *   pow(snoise(), 3) でスパイク的なエンベロープを生成
   *   = 「入った！」→ 無音 →「左！」→ 無音 のリズム
   * - 6層の周波数レイヤーで豊かなスペクトル（人間の声は複合波）
   * - マウスX位置に巨大な発話バーストを発生（声が入った瞬間）
   *
   * なぜこの表現か:
   * - ShootCreatorは「シュートしながら独り言を言い続ける」アプリ
   * - 波形は常に流れているのではなく、断続的に湧き上がるもの
   * - 大きく画面を使うことで、音声がプロダクトの主役であることを伝える
   */
  {
    id: 1,
    name: "Voice Stream",
    nameJa: "音声ストリーム",
    motif: "ShootCreator is always listening — continuous audio capture",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time;
  vec3 color = vec3(0.0);

  float centerY = 0.5;
  float dy = uv.y - centerY;
  float absdy = abs(dy);

  // ══ 発話バースト — 時間駆動エンベロープ ══
  // 「入った」→沈黙→「左！」→沈黙→「ショート」
  // 重要: uv.x（空間）ではなく t（時間）で駆動 → ON/OFFが明確に出る

  // チャネル1: メインの発話リズム — ゆったりした呼吸のような起伏
  float v1 = snoise(vec3(0.0, 0.0, t * 0.6));
  v1 = smoothstep(-0.1, 0.35, v1);  // 広い閾値 = 緩やかなON/OFF

  // チャネル2: 発話の抑揚（「左、もうちょい…」）
  float v2 = snoise(vec3(4.0, 2.0, t * 1.0));
  v2 = smoothstep(0.05, 0.4, v2);

  // チャネル3: 稀な強調（「入ったー！」）— 控えめに
  float v3 = snoise(vec3(9.0, 5.0, t * 0.4));
  v3 = smoothstep(0.25, 0.55, v3);
  v3 *= 0.7;

  // 統合: 滑らかにブレンド（maxではなく加算mix）
  float speech = v1 * 0.5 + v2 * 0.3 + v3 * 0.2;

  // 空間的な変化（ゆっくり流れる）
  float spatial = snoise(vec3(uv.x * 1.5 - t * 0.8, 0.5, t * 0.1)) * 0.2 + 0.8;
  speech *= spatial;

  // マウス = 控えめなインタラクション
  float mx = smoothstep(0.3, 0.0, abs(uv.x - u_mouse.x));
  speech = max(speech, mx * 0.6);

  // 振幅: 画面の20%程度に抑え、常に微かな波がある
  float amp = speech * 0.20 + 0.008;

  // ══ 4層の周波数レイヤー（落ち着いた声のスペクトル） ══
  for (int i = 0; i < 4; i++) {
    float fi = float(i);

    float freq = 4.0 + fi * 3.5;
    float speed = t * (1.8 + fi * 0.6);

    // 有機的な波形
    float wave = sin(uv.x * freq - speed + fi * 1.7);
    wave += snoise(vec3(uv.x * freq * 0.3 - t * 1.0, fi * 3.0, t * 0.2)) * 0.4;
    wave += sin(uv.x * freq * 1.8 - speed * 1.3) * 0.15;

    float layerAmp = amp * (1.0 - fi * 0.15) * (0.5 + wave * 0.5);
    layerAmp = max(layerAmp, 0.0);

    float edgeDist = abs(absdy - layerAmp);
    float edge = smoothstep(0.003, 0.0, edgeDist);
    float glow = smoothstep(0.025, 0.0, edgeDist) * 0.35;
    float fill = smoothstep(layerAmp + 0.003, 0.0, absdy) * 0.10;

    float opacity = 1.0 - fi * 0.18;
    vec3 layerColor = mix(u_color1, u_color2, fi / 3.0);
    color += layerColor * (edge * 0.12 + glow * 0.06 + fill * 0.03) * opacity;
  }

  // ベースライン（常時録音中の微かな光）
  float baseline = smoothstep(0.002, 0.0, absdy);
  color += mix(u_color1, u_color2, 0.5) * baseline * 0.04;

  // 背景ノイズフロア（マイクの環境音）
  float bgNoise = fbm(uv * 4.0, t * 0.08) * 0.5 + 0.5;
  color += mix(u_color1, u_color2, 0.5) * bgNoise * 0.01;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 2. Transcription ━━━
   *
   * ストーリー: 音声(ノイズ)がテキスト(シグナル)に変換される過程。
   *
   * 技法:
   * - Inigo Quilezのダブルドメインワーピング
   *   fbm(p + fbm(p + fbm(p))) — ノイズの入力をノイズで歪める2段重ね
   * - warpStrength をスクロールで制御:
   *   上(scroll=0) = 強いワープ = 混沌(生の音声)
   *   下(scroll=1) = 弱いワープ = 秩序(構造化テキスト)
   * - マウス周辺に「clarityレンズ」: 局所的にワープを抑制 = その場所だけ秩序化
   * - 下層に水平線パターン(テキスト行を暗示)が透けて見える
   *
   * なぜこの表現か:
   * - 「ノイズ→シグナル」がShootCreatorの本質的メタファーそのもの
   * - スクロール = ユーザーがコンテンツを読み進める = データに触れる体験
   * - ページを読むにつれて背景が整っていく = 製品理解が深まる暗示
   */
  {
    id: 2,
    name: "Transcription",
    nameJa: "文字起こし",
    motif: "Voice becomes text — chaos resolves into structured signal",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.06;
  vec2 p = uv * 3.0;  // UV座標を3倍に拡大(模様のスケール)

  // スクロールでワープ強度を減衰: 1.0(混沌) → 0.25(秩序)
  float warpStrength = 1.0 - u_scroll * 0.75;

  // マウス周辺は局所的にワープを抑制(clarityレンズ)
  float mouseDist = length(uv - u_mouse);
  float clarity = smoothstep(0.25, 0.0, mouseDist);
  warpStrength *= (1.0 - clarity * 0.6);

  // ── ダブルドメインワーピング ──
  // 第1段: 基本ワープベクトル
  vec2 q = vec2(
    fbm(p + vec2(0.0, 0.0), t),
    fbm(p + vec2(5.2, 1.3), t)   // オフセットで異なるパターン
  );
  // 第2段: q でワープした座標をさらにワープ
  vec2 r = vec2(
    fbm(p + 4.0 * q * warpStrength + vec2(1.7, 9.2), t * 1.2),
    fbm(p + 4.0 * q * warpStrength + vec2(8.3, 2.8), t * 1.2)
  );
  // 最終FBM: 2段ワープ後の複雑なパターン
  float f = fbm(p + 4.0 * r * warpStrength, t * 0.7);

  // 下層の水平線パターン(テキスト行を暗示)
  // warpStrengthが低い(=秩序化)ほど線が明瞭に見える
  float lines = smoothstep(0.1, 0.04, abs(fract(uv.y * 18.0 + f * warpStrength * 0.5) - 0.5));
  float structure = lines * (1.0 - warpStrength) * 0.12;

  // ワープベクトルの長さで色をブレンド
  vec3 color = vec3(0.0);
  color = mix(color, u_color1 * 0.7, clamp(length(q) * 0.5, 0.0, 1.0));
  color = mix(color, u_color2 * 0.6, clamp(length(r) * 0.4, 0.0, 1.0));
  color += u_color1 * clamp(f * f * 3.0, 0.0, 1.0) * 0.12;
  color += mix(u_color1, u_color2, 0.5) * structure;
  color *= 0.35;  // 全体を暗めに(screen blend modeで明るくなりすぎない)

  // マウス周辺に微光(注目領域のハイライト)
  color += mix(u_color1, u_color2, 0.5) * clarity * 0.04;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 3. Classification ━━━
   *
   * ストーリー: ミスがArc(弧度)/Depth(深度)/Direction(方向)に分類される。
   *
   * 技法:
   * - FBM高度フィールドを3色ゾーンに分割
   *   低高度 = 赤(Arc) → 中高度 = 緑(OK) → 高高度 = 青(Depth)
   * - smoothstepで滑らかにゾーン遷移(ハードな境界ではなくグラデーション)
   * - 等高線(contour lines)でデータの「地形図」を表現
   * - ゾーン境界に白いラインで分類の区切りを強調
   *
   * なぜこの表現か:
   * - 色はTriColorGaugeと同一カラーシステム
   *   赤(#ff3b30)=COLOR_EXTREME, 緑(#34c759)=COLOR_OK, 青(#0071e3)=AIブランド
   * - 等高線はデータ可視化(ヒートマップ, 地形図)の定番表現
   * - マウス接近で等高線が明瞭化 = データに注目すると詳細が見える
   */
  {
    id: 3,
    name: "Classification",
    nameJa: "ミス分類",
    motif: "Errors classified into Arc / Depth / Direction zones",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.05;
  vec2 p = uv * 2.5;
  p += (u_mouse - 0.5) * 0.15;  // マウスで地形がわずかにシフト

  // FBMで高度フィールドを生成
  float height = fbm(p, t + u_scroll * 0.3);
  float h = height * 0.5 + 0.5;  // -1~1 を 0~1 に正規化

  // TriColorGaugeと同じカラーシステム
  vec3 arcColor = vec3(1.0, 0.23, 0.19);    // Arc: 赤 #ff3b30
  vec3 okColor = vec3(0.2, 0.78, 0.35);     // OK: 緑 #34c759
  vec3 depthColor = vec3(0.0, 0.44, 0.89);  // Depth: 青 #0071e3

  // 高度による色の滑らかな遷移 (赤 → 緑 → 青)
  vec3 zoneColor = arcColor;
  zoneColor = mix(zoneColor, okColor, smoothstep(0.25, 0.42, h));
  zoneColor = mix(zoneColor, depthColor, smoothstep(0.58, 0.75, h));

  // 等高線の計算
  float contourN = 12.0 + u_scroll * 8.0;  // スクロールで等高線密度増加
  float contour = abs(fract(height * contourN) - 0.5);
  float contourLine = smoothstep(0.03, 0.0, contour);

  // ゾーン境界線 (3分割の境界)
  float zoneBoundary = abs(fract(h * 3.0) - 0.5);
  float zoneLine = smoothstep(0.04, 0.0, zoneBoundary);

  // 描画: 薄いゾーン色 + 等高線 + ゾーン境界
  vec3 color = zoneColor * 0.04;                  // 背景のゾーン色(とても薄い)
  color += zoneColor * contourLine * 0.12;        // 等高線
  color += vec3(1.0) * zoneLine * 0.08;           // ゾーン境界(白)

  // マウス近傍で等高線を強調
  float md = length(uv - u_mouse);
  color += zoneColor * smoothstep(0.2, 0.0, md) * contourLine * 0.1;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 4. Pattern Discovery ━━━
   *
   * ストーリー: データの中から繰り返しパターンが浮かび上がる瞬間。
   *
   * 技法:
   * - Voronoi図 (空間をセルに分割)
   *   各ピクセルから最も近い/2番目に近いセル中心への距離を計算
   *   secondD - minD = セル境界(エッジ)までの距離
   * - FBMワープでセルが有機的に歪む
   * - マウスが「発見レンズ」として機能:
   *   マウスに近い領域: エッジが明るく光る(パターンが見えている)
   *   マウスから遠い領域: 暗い(まだ発見されていないデータ)
   * - エッジ上をパルスが走る(信号伝達の暗示)
   *
   * なぜこの表現か:
   * - Voronoiはデータセグメンテーション/クラスタリングの視覚メタファー
   * - 「注目するとパターンが見える」= ユーザーがデータを分析する行為そのもの
   * - awwwards受賞サイト(Igloo Inc)でもVoronoiベースのビジュアルが評価されている
   */
  {
    id: 4,
    name: "Pattern Discovery",
    nameJa: "パターン発見",
    motif: "Mouse reveals hidden patterns — data segmentation emerges",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      HASH,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.08;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);  // アスペクト比補正

  // FBMで空間を有機的に歪める(ドメインワーピング)
  vec2 warp = vec2(
    fbm(p * 1.5, t),
    fbm(p * 1.5 + 4.7, t)  // 異なるオフセットで独立なワープ
  );
  p += warp * 0.15;

  // Voronoi: スケール可変 (スクロールでセル数増加 = データ量増加)
  float scale = 5.0 + u_scroll * 2.0;
  p *= scale;
  vec2 ip = floor(p);  // 現在のグリッドセルID
  vec2 fp = fract(p);  // セル内の相対座標

  // 最近傍 (minD) と2番目に近い (secondD) セル中心を探索
  float minD = 10.0;
  float secondD = 10.0;
  vec2 minId = vec2(0.0);

  // 3x3近傍のセルを走査
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 nb = vec2(float(x), float(y));
      vec2 cid = ip + nb;
      vec2 h = hash2(cid);  // セルIDからランダムな位置を生成
      // セル中心がゆっくり周回運動
      vec2 center = nb + vec2(
        sin(t * 3.0 + h.x * 6.28) * 0.35 + 0.5,
        cos(t * 2.5 + h.y * 6.28) * 0.35 + 0.5
      );

      float d = length(fp - center);
      if (d < minD) { secondD = minD; minD = d; minId = cid; }
      else if (d < secondD) { secondD = d; }
    }
  }

  // セル境界(エッジ)までの距離
  float edgeDist = secondD - minD;
  float edgeLine = smoothstep(0.06, 0.0, edgeDist);  // エッジの線
  float edgeGlow = smoothstep(0.15, 0.0, edgeDist) * 0.5;  // エッジのグロー

  // マウスが「発見レンズ」: 近いほどパターンが可視化される
  float md = length(uv - u_mouse);
  float lens = smoothstep(0.35, 0.0, md);  // 0(遠い)~1(近い)

  float cellVal = hash(minId);  // セルごとのランダム値(色分け用)
  vec3 edgeColor = mix(u_color1, u_color2, cellVal);

  // 描画: レンズ効果で明暗をコントロール
  vec3 color = vec3(0.0);
  color += edgeColor * edgeLine * (0.06 + lens * 0.25);  // 遠い=薄い, 近い=濃い
  color += edgeColor * edgeGlow * (0.02 + lens * 0.08);
  color += edgeColor * smoothstep(0.4, 0.0, minD) * lens * 0.04;  // セル中心の光

  // エッジ上を走るパルス(信号伝達)
  float pulse = sin(edgeDist * 40.0 - u_time * 2.0) * 0.5 + 0.5;
  color += edgeColor * pulse * edgeLine * lens * 0.04;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 5. Signal Pulse ━━━
   *
   * ストーリー: ShootCreatorの「心拍」— リアルタイムデータが脈打つ。
   *
   * 技法:
   * - 同心円リング: sin(距離 * 周波数 - 時間 * 速度) でリング模様
   * - ドメインワーピングで有機的に歪める
   * - 心拍リズム: pow(sin(t), 8) で急峻なスパイク波形を生成
   *   (sin波の8乗 → 鋭いピーク = ECGのQRS波に似た形)
   * - 心拍に連動してリング速度とコアグローが変化
   * - 中央を横切るECG波形ライン
   *
   * なぜこの表現か:
   * - バイオメトリックデータ(心拍)はShootCreator AIの差別化要素
   * - パルスリング = データが「生きている」感覚
   * - ECGライン = 心拍モニターの直感的な連想
   */
  {
    id: 5,
    name: "Signal Pulse",
    nameJa: "シグナルパルス",
    motif: "ShootCreator's heartbeat — real-time biometric data flow",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.1;
  float aspect = u_resolution.x / u_resolution.y;
  // リング中心: マウスに少し追従(生体センサーの位置感)
  vec2 center = vec2(0.5 + (u_mouse.x - 0.5) * 0.12, 0.5 + (u_mouse.y - 0.5) * 0.12);
  vec2 p = uv - center;
  p.x *= aspect;  // アスペクト比補正(円が楕円にならない)

  float dist = length(p);  // 中心からの距離

  // FBMワープで同心円を有機的に歪める
  vec2 warp = vec2(
    fbm(p * 3.0 + vec2(1.3, 7.1), t),
    fbm(p * 3.0 + vec2(8.2, 3.9), t)
  );
  float warpedDist = dist + length(warp) * 0.08;

  // 心拍リズム: sin()の8乗で急峻なスパイク
  float heartbeat = sin(u_time * 1.2) * 0.5 + 0.5;
  heartbeat = pow(heartbeat, 8.0);  // 鋭いピーク → ECGのR波に似た形
  float ringSpeed = 2.0 + heartbeat * 3.0;   // 心拍時にリング加速
  float ringFreq = 15.0 + u_scroll * 5.0;    // スクロールでリング密度変化

  // 同心円リング
  float rings = sin(warpedDist * ringFreq - u_time * ringSpeed) * 0.5 + 0.5;
  rings = smoothstep(0.4, 0.6, rings);  // リングを鮮明化
  rings *= smoothstep(0.8, 0.1, dist);  // 画面端でフェードアウト

  // 中心のコアグロー(心拍に連動)
  float coreGlow = smoothstep(0.15, 0.0, dist) * (0.3 + heartbeat * 0.5);

  // ── ECG波形ライン(中央を横切る心電図) ──
  float ecgX = (uv.x - center.x) * aspect;
  // ノイズベースの揺らぎ + 心拍時のスパイク
  float ecgWave = snoise(vec3(ecgX * 8.0 - u_time * 2.0, 0.0, t * 0.5)) * 0.03;
  ecgWave += heartbeat * exp(-abs(ecgX) * 8.0) * 0.06;  // exp()で中心のみスパイク
  float ecgDist = abs(p.y - ecgWave);
  float ecg = smoothstep(0.003, 0.0, ecgDist) * smoothstep(0.6, 0.2, abs(ecgX));

  // 色の合成
  vec3 ringColor = mix(u_color1, u_color2, warpedDist * 2.0);
  vec3 color = ringColor * rings * 0.18;
  color += mix(u_color1, u_color2, 0.3) * coreGlow * 0.18;
  color += mix(u_color1, u_color2, 0.5) * ecg * 0.2;

  // 外周の淡いワープグロー
  float outerFade = smoothstep(0.7, 0.3, dist);
  float warpGlow = clamp(length(warp) * 0.5, 0.0, 1.0);
  color += mix(u_color1, u_color2, warpGlow) * outerFade * 0.03;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 6. Neural Flow ━━━
   *
   * ストーリー: AIがデータ間の相関関係を発見する — ニューラルネットワーク。
   *
   * 技法:
   * - Voronoiのセル中心 = ニューロン(ノード)
   * - Voronoiのエッジ = シナプス(接続)
   * - ノードは明るい点、エッジは細い光のライン
   * - マウス接近で「ニューラル活性化」が伝播
   *   近い領域のノードとエッジが強く光る
   * - エッジ上をパルスが走る(信号伝達)
   *
   * なぜこの表現か:
   * - ShootCreator AIはユーザーの発話パターンから相関を見出す
   * - ニューラルネットワークの視覚化 = AIの判断プロセスの暗示
   * - Pattern Discovery(#4)との違い:
   *   #4はマウスレンズで「発見」, #6はネットワーク全体の「活性化」
   */
  {
    id: 6,
    name: "Neural Flow",
    nameJa: "ニューラルフロー",
    motif: "AI finding correlations — neural network activation",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      HASH,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.08;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  // FBMワープでネットワーク全体が有機的に流動
  vec2 warp = vec2(
    fbm(p * 1.5, t),
    fbm(p * 1.5 + 4.7, t)
  );
  p += warp * 0.1;

  float scale = 5.0;
  p *= scale;
  vec2 ip = floor(p);
  vec2 fp = fract(p);

  // Voronoi: 最近傍/第2近傍を探索
  float minD = 10.0;
  float secondD = 10.0;
  vec2 minCenter = vec2(0.0);
  vec2 minId = vec2(0.0);

  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 nb = vec2(float(x), float(y));
      vec2 cid = ip + nb;
      vec2 h = hash2(cid);
      vec2 center = nb + vec2(
        sin(t * 2.5 + h.x * 6.28) * 0.3 + 0.5,
        cos(t * 2.0 + h.y * 6.28) * 0.3 + 0.5
      );

      float d = length(fp - center);
      if (d < minD) { secondD = minD; minD = d; minCenter = center; minId = cid; }
      else if (d < secondD) { secondD = d; }
    }
  }

  float edgeDist = secondD - minD;

  // ニューロン(ノード): セル中心の明るい点
  float nodeDist = minD;
  float node = smoothstep(0.08, 0.0, nodeDist);
  float nodeGlow = smoothstep(0.18, 0.0, nodeDist) * 0.4;

  // シナプス(接続): エッジの光るライン
  float edgeLine = smoothstep(0.04, 0.0, edgeDist);
  float edgeGlow = smoothstep(0.12, 0.0, edgeDist) * 0.35;

  // マウス接近で「ニューラル活性化」
  float md = length(uv - u_mouse);
  float activation = smoothstep(0.3, 0.0, md);

  float cellVal = hash(minId);
  vec3 nodeColor = mix(u_color1, u_color2, cellVal);

  // 描画: 活性化度に応じて明るさが変化
  vec3 color = vec3(0.0);
  color += nodeColor * edgeLine * (0.06 + activation * 0.18);
  color += nodeColor * edgeGlow * (0.02 + activation * 0.05);
  color += nodeColor * node * (0.08 + activation * 0.14);
  color += nodeColor * nodeGlow * (0.02 + activation * 0.03);

  // エッジ上を走るパルス(信号伝達)
  float pulse = sin(edgeDist * 35.0 - u_time * 2.5 + cellVal * 6.28) * 0.5 + 0.5;
  color += nodeColor * pulse * edgeLine * 0.04;

  // スクロールでネットワーク全体がわずかにグロー
  float scrollGlow = u_scroll * smoothstep(0.6, 0.0, nodeDist);
  color += mix(u_color1, u_color2, 0.5) * scrollGlow * 0.03;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 7. Ink Flow ━━━
   *
   * ストーリー: 分析前の「生のデータ」の有機的な美しさ。ブランドの世界観。
   *
   * 技法:
   * - Inigo Quilezのダブルドメインワーピング (最も有機的なFBMパターン)
   *   q = fbm(p)
   *   r = fbm(p + 4*q)
   *   f = fbm(p + 4*r)
   *   この3段重ねが墨流し/マーブリングのような模様を生む
   * - ワープベクトルの長さ(length(q), length(r))で色をブレンド
   *
   * なぜこの表現か:
   * - 汎用的なブランドビジュアルとして最も洗練された表現
   * - 10プリセット中、最も「ジェネリック」で、あらゆるページに合う
   * - awwwards受賞サイトの多くがドメインワーピングを背景に採用
   */
  {
    id: 7,
    name: "Ink Flow",
    nameJa: "墨流し",
    motif: "Raw data beauty — organic flow before analysis",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.06;
  float s = u_scroll * 0.3;
  vec2 p = uv * 3.0;
  p += (u_mouse - 0.5) * 0.4;  // マウスで全体がドリフト

  // ── ダブルドメインワーピング ──
  // 第1段ワープ
  vec2 q = vec2(
    fbm(p + vec2(0.0, 0.0), t),
    fbm(p + vec2(5.2, 1.3), t)
  );
  // 第2段ワープ (スクロールで時間軸をオフセット)
  vec2 r = vec2(
    fbm(p + 4.0 * q + vec2(1.7, 9.2), t * 1.3 + s),
    fbm(p + 4.0 * q + vec2(8.3, 2.8), t * 1.3 + s)
  );
  // 最終パターン
  float f = fbm(p + 4.0 * r, t * 0.7);

  // ワープベクトルの長さで色を段階的にブレンド
  vec3 color = vec3(0.0);
  color = mix(color, u_color1 * 0.9, clamp(length(q) * 0.7, 0.0, 1.0));
  color = mix(color, u_color2 * 0.8, clamp(length(r) * 0.6, 0.0, 1.0));
  color += u_color1 * clamp(f * f * 3.0, 0.0, 1.0) * 0.2;
  color *= 0.35;  // screen blend mode用に暗めに

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 8. Convergence ━━━
   *
   * ストーリー: 散らばるミスデータが統計的にまとまっていく — データ集約。
   *
   * 技法:
   * - 20個のパーティクルがノイズ変位軌道で有機的に漂う
   * - 各パーティクルに速度方向のモーショントレイル（残像）
   * - 3層のグロー（コア / ソフト / アンビエント）で立体感
   * - ノイズフローフィールド背景 — 散逸時は乱流、収束時は中心求心
   * - エネルギースレッド: 中心に向かう繊維状の流線
   * - パルスリング: 収束完了で呼吸するリング
   * - 収束度 = scroll³（キュービックイージング、後半で急収束）
   */
  {
    id: 8,
    name: "Convergence",
    nameJa: "収束",
    motif: "Scattered misses converge into statistical insight",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.04;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  // 収束度: scroll³ キュービックイージング
  float conv = u_scroll * u_scroll * u_scroll;
  vec3 color = vec3(0.0);

  // ── フローフィールド背景 ──
  // 散逸時: 乱流ノイズ / 収束時: 中心に向かう求心的な流れ
  vec2 flowUV = p * 3.0;
  float flowAngle = snoise(vec3(flowUV, t * 0.3)) * 6.2832;
  // 求心ベクトル: -normalize(p)方向にconv比例で寄せる
  float toCenter = atan(p.y, p.x);
  flowAngle = mix(flowAngle, toCenter + 3.14159, conv * 0.8);
  vec2 flowDir = vec2(cos(flowAngle), sin(flowAngle));
  // 流線のストリーク (フローに沿ったノイズ)
  float streak = snoise(vec3(flowUV + flowDir * t * 2.0, t * 0.15));
  streak = abs(streak);
  streak = pow(streak, 3.0) * 0.04 * (0.3 + conv * 0.7);
  color += mix(u_color1, u_color2, streak * 8.0) * streak;

  // ── 20個のパーティクル（ノイズ変位軌道 + モーショントレイル） ──
  for (int i = 0; i < 20; i++) {
    float fi = float(i);
    float seed = fi * 7.31;
    float angle = fi * 0.31416 + sin(seed) * 0.5;  // 黄金角ベース
    float baseR = 0.22 + sin(seed * 1.3) * 0.12;
    float orbitSpeed = 0.15 + sin(seed * 0.7) * 0.08;

    // ノイズ変位: 単純な円軌道ではなく不規則な揺らぎ
    float nOff = snoise(vec3(fi * 2.0, t * orbitSpeed, seed)) * 0.08;

    vec2 scattered = vec2(
      cos(angle + t * orbitSpeed) * (baseR + nOff),
      sin(angle + t * orbitSpeed * 0.85) * (baseR + nOff) * 0.9
    );

    // 収束: キュービックに中心へ
    vec2 pos = scattered * (1.0 - conv);

    // 速度ベクトル (トレイル方向)
    float dt = 0.008;
    float nOff2 = snoise(vec3(fi * 2.0, (t + dt) * orbitSpeed, seed)) * 0.08;
    vec2 scattered2 = vec2(
      cos(angle + (t + dt) * orbitSpeed) * (baseR + nOff2),
      sin(angle + (t + dt) * orbitSpeed * 0.85) * (baseR + nOff2) * 0.9
    );
    vec2 pos2 = scattered2 * (1.0 - conv);
    vec2 vel = normalize(pos2 - pos + vec2(0.0001));

    // トレイル: 速度方向に引き延ばした楕円距離
    vec2 toP = p - pos;
    float along = dot(toP, vel);
    float perp = length(toP - vel * along);
    float trailLen = 0.06 * (1.0 - conv);  // 収束でトレイル消失
    float core = smoothstep(0.008, 0.0, perp) * smoothstep(trailLen, 0.0, abs(along))
               * step(0.0, -along);  // 後方にのみトレイル

    float dist = length(toP);
    // 3層グロー
    float bright = smoothstep(0.012, 0.0, dist);       // コア
    float mid = smoothstep(0.04, 0.0, dist) * 0.4;     // ソフトグロー
    float ambient = smoothstep(0.10, 0.0, dist) * 0.08; // アンビエント

    // サイズ: 個体差 (大小混在で奥行き感)
    float sizeMul = 0.7 + sin(seed * 3.1) * 0.5;
    float ptVal = (bright + mid + ambient + core * 0.3) * sizeMul;

    // 色: グラデーション + 個体差のオフセット
    float colorT = fract(fi / 19.0 + sin(seed) * 0.2);
    vec3 ptColor = mix(u_color1, u_color2, colorT);

    color += ptColor * ptVal * 0.14;
  }

  // ── エネルギースレッド（中心に向かう繊維） ──
  float centerDist = length(p);
  float angleP = atan(p.y, p.x);
  // 放射状のストライプ (角度方向のノイズ)
  float threadNoise = snoise(vec3(angleP * 3.0, centerDist * 8.0 - t * 3.0, t * 0.2));
  float threads = pow(max(threadNoise, 0.0), 4.0);
  // 中心近くで強く、遠くで弱い + 収束度に比例
  float threadMask = smoothstep(0.35, 0.05, centerDist) * conv;
  color += mix(u_color1, u_color2, 0.6) * threads * threadMask * 0.10;

  // ── 中心グロー（多層、収束度に比例） ──
  float cg1 = smoothstep(0.20, 0.0, centerDist) * conv;
  float cg2 = smoothstep(0.08, 0.0, centerDist) * conv;
  float pulse = 1.0 + sin(u_time * 1.5) * 0.15 * conv;
  color += mix(u_color1, u_color2, 0.3) * cg1 * 0.12 * pulse;
  color += mix(u_color1, u_color2, 0.7) * cg2 * 0.08;

  // ── パルスリング（収束で出現、呼吸する） ──
  float ringR = 0.14 + sin(u_time * 0.8) * 0.02 * conv;
  float ringW = abs(centerDist - ringR);
  float ring = smoothstep(0.005, 0.0, ringW) * conv * 0.6;
  // 回転ダッシュ: 角度方向に断続的
  float dash = sin(angleP * 8.0 + u_time * 0.5) * 0.5 + 0.5;
  dash = smoothstep(0.3, 0.7, dash);
  color += mix(u_color1, u_color2, 0.5) * ring * dash * 0.12;

  // 第2リング（小さく、逆回転）
  float ring2R = 0.06 * conv;
  float ring2W = abs(centerDist - ring2R);
  float ring2 = smoothstep(0.003, 0.0, ring2W) * conv * 0.4;
  float dash2 = sin(angleP * 12.0 - u_time * 0.7) * 0.5 + 0.5;
  color += mix(u_color1, u_color2, 0.8) * ring2 * dash2 * 0.08;

  // ── マウスインタラクション ──
  vec2 mouseP = vec2((u_mouse.x - 0.5) * aspect, u_mouse.y - 0.5);
  float md = length(p - mouseP);
  float mouseGlow = smoothstep(0.12, 0.0, md);
  color += mix(u_color1, u_color2, 0.5) * mouseGlow * 0.04;

  // ── 背景ノイズ (散逸時の乱雑さ) ──
  float bgN = fbm(p * 5.0, t * 0.5) * 0.5 + 0.5;
  color += mix(u_color1, u_color2, 0.4) * bgN * 0.006 * (1.0 - conv * 0.8);

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 9. Heatmap ━━━
   *
   * ストーリー: コート上のシュートチャート — パフォーマンスの熱分布。
   *
   * 技法:
   * - 3層のFBMを異なるスケール/速度で重ね合わせて熱分布を生成
   *   大スケール(×2.0) + 中スケール(×3.5) + 小スケール(×5.0)
   * - 5色の温度ランプ: 深青→シアン→緑→黄→赤
   *   smoothstepで滑らかに遷移(ハードな色段差なし)
   * - マウスが新しい「熱源」を生成(タッチした場所が温まる)
   * - スクロールで全体の熱量が上昇
   * - 等高線(contour)で温度分布を強調
   *
   * なぜこの表現か:
   * - バスケのシュートチャートはヒートマップが定番
   *   courtZones.tsの5グループ色(restricted=#ff4444, paint=#ff6b00,
   *   mid=#ffab00, 3pt=#00c853, deep=#2979ff)を抽象化した温度表現
   * - webgl-heatmap(pyalot)ライブラリからの着想
   *   GPU上でaddPoint→accumulate→colorMapのパイプライン
   */
  {
    id: 9,
    name: "Heatmap",
    nameJa: "ヒートマップ",
    motif: "Court shot chart — performance heat zones",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.05;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  // 3層のFBMで熱分布を生成(異なるスケール/方向/速度)
  float heat = 0.0;
  heat += fbm(p * 2.0 + vec2(t * 0.3, t * 0.2), t) * 0.5;       // 大域的な熱
  heat += fbm(p * 3.5 + vec2(-t * 0.2, t * 0.15), t * 0.7) * 0.3; // 中域的な変動
  heat += fbm(p * 5.0 + vec2(t * 0.1, -t * 0.25), t * 1.2) * 0.2; // 局所的な揺らぎ

  // マウス = 新しい熱源(タッチした場所が温まる)
  vec2 mouseP = vec2((u_mouse.x - 0.5) * aspect, u_mouse.y - 0.5);
  float mouseDist = length(p - mouseP);
  heat += smoothstep(0.2, 0.0, mouseDist) * 0.25;

  // スクロールで全体の熱量上昇
  heat *= 0.8 + u_scroll * 0.4;
  heat = clamp(heat * 0.5 + 0.5, 0.0, 1.0);  // 0~1に正規化

  // ── 5色温度ランプ ──
  // コートゾーンカラー(deep=青, 3pt=緑, mid=黄, paint=橙, restricted=赤)を
  // 抽象化した温度グラデーション
  vec3 c1 = vec3(0.0, 0.16, 0.55);   // 深青 (cold/deep zone)
  vec3 c2 = vec3(0.0, 0.55, 0.7);    // シアン
  vec3 c3 = vec3(0.2, 0.78, 0.35);   // 緑 (three-point zone)
  vec3 c4 = vec3(1.0, 0.75, 0.0);    // 黄/金 (mid-range zone)
  vec3 c5 = vec3(1.0, 0.23, 0.19);   // 赤 (hot/restricted zone)

  // smoothstepで滑らかに遷移(ハードな色段差なし)
  vec3 heatColor = c1;
  heatColor = mix(heatColor, c2, smoothstep(0.15, 0.3, heat));
  heatColor = mix(heatColor, c3, smoothstep(0.3, 0.5, heat));
  heatColor = mix(heatColor, c4, smoothstep(0.5, 0.7, heat));
  heatColor = mix(heatColor, c5, smoothstep(0.7, 0.88, heat));

  vec3 color = heatColor * 0.18;  // screen blend用に暗めに

  // ホットスポット(高温部)の追加グロー
  float hotSpot = smoothstep(0.72, 0.92, heat);
  color += heatColor * hotSpot * 0.08;

  // 等高線(温度分布の境界を可視化)
  float contour = abs(fract(heat * 8.0) - 0.5);
  float contourLine = smoothstep(0.03, 0.0, contour);
  color += heatColor * contourLine * 0.04;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },

  /**
   * ━━━ 10. Court Grid ━━━
   *
   * ストーリー: 構造化データ(グリッド) × 人間のパフォーマンス(有機的歪み)。
   *
   * 技法:
   * - 精密グリッドライン(データの構造性)
   * - FBMワープで有機的に歪める(人間のパフォーマンスの不確実性)
   * - バスケットコート要素をSDF(符号付き距離関数)で描画:
   *   - 3ポイントアーク: 円のSDF abs(length(p) - radius)
   *   - ペイントエリア: 矩形のSDF
   *   - フリースロー円: 円のSDF
   * - マウス接近でワープ量増加(人間の影響が強まる)
   * - マウスからの距離に応じたパルス波
   *
   * なぜこの表現か:
   * - shoot-creater(Classic)の19ゾーンSVGコートをシェーダーで抽象表現
   * - SVGパスの完全再現ではなく、SDFによる「コートらしさ」の暗示
   * - グリッド(精密な分析) vs FBMワープ(有機的な人間性)の対比
   * - 実際のコートSVGはDOMレイヤー(BiometricDashboard等)で使用
   */
  {
    id: 10,
    name: "Court Grid",
    nameJa: "コートグリッド",
    motif: "Structured data meets human performance — court analysis",
    fragment: frag(
      HEADER,
      NOISE,
      FBM,
      POST,
      `void main() {
  vec2 uv = vUv;
  float t = u_time * 0.06;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  // マウス距離に応じたワープ量(近い=歪みが強い=人間の影響)
  vec2 mouseP = vec2((u_mouse.x - 0.5) * aspect, u_mouse.y - 0.5);
  float mouseDist = length(p - mouseP);
  float warpAmt = smoothstep(0.5, 0.0, mouseDist) * 0.06 + 0.012;

  // FBMワープ(構造化データに有機的な歪みを加える)
  vec2 warp = vec2(
    fbm(p * 3.0, t),
    fbm(p * 3.0 + 5.1, t)
  ) * warpAmt;
  vec2 wp = p + warp;  // ワープ後の座標

  // ── グリッドライン (構造化データの表現) ──
  float density = 12.0 + u_scroll * 6.0;  // スクロールでグリッド密度増加
  vec2 grid = abs(fract(wp * density) - 0.5);
  float gridLine = smoothstep(0.018, 0.0, min(grid.x, grid.y));

  // ── バスケットコート要素 (SDFで抽象表現) ──

  // 3ポイントアーク: 円のSDF
  float arcRadius = 0.28;
  float arcDist = abs(length(wp) - arcRadius);
  float arc = smoothstep(0.006, 0.0, arcDist);

  // ペイントエリア: 矩形のSDF
  vec2 paintHalf = vec2(0.09, 0.18);          // 半分のサイズ
  vec2 paintCenter = vec2(0.0, -0.05);        // 少し下にオフセット
  vec2 pd = abs(wp - paintCenter) - paintHalf;
  float paintOuter = length(max(pd, 0.0)) + min(max(pd.x, pd.y), 0.0);
  float paint = smoothstep(0.006, 0.0, abs(paintOuter));

  // フリースロー円: 円のSDF
  float ftRadius = 0.09;
  vec2 ftCenter = vec2(0.0, -0.05 + 0.18);   // ペイント上端
  float ftDist = abs(length(wp - ftCenter) - ftRadius);
  float ft = smoothstep(0.005, 0.0, ftDist);

  // コート要素を合成 (max = いずれかが描画されればOK)
  float courtElements = max(arc, max(paint, ft));

  // ── 色の計算 ──
  // ワープ量で色変化(歪みが強い部分はcolor2寄り)
  float distort = length(warp) * 15.0;
  vec3 gridColor = mix(u_color1, u_color2, clamp(distort, 0.0, 1.0));

  vec3 color = gridColor * gridLine * 0.1;                    // グリッドライン
  color += mix(u_color1, u_color2, 0.5) * courtElements * 0.22; // コート要素

  // マウスからのパルス波(リプルエフェクト)
  float pulse = sin(mouseDist * 22.0 - u_time * 3.0) * 0.5 + 0.5;
  pulse *= smoothstep(0.5, 0.0, mouseDist) * 0.08;
  color += gridColor * pulse;

  // 大きなグリッドの交差点(主要データポイントのマーカー)
  float crosshair = smoothstep(0.012, 0.0, abs(fract(wp.x * density * 0.25) - 0.5))
                   * smoothstep(0.012, 0.0, abs(fract(wp.y * density * 0.25) - 0.5));
  color += mix(u_color1, u_color2, 0.5) * crosshair * 0.12;

  gl_FragColor = vec4(post(color, uv), 1.0);
}`,
    ),
  },
];
